const larguraPagina = 595
const alturaPagina = 842
const margem = 36
const larguraUtil = larguraPagina - margem * 2

const dataFormatada = (valor) => {
  if (!valor) return ''
  const data = new Date(valor)
  return Number.isNaN(data.getTime()) ? '' : data.toLocaleDateString('pt-BR')
}

const idade = (valor) => {
  if (!valor) return ''
  const nascimento = new Date(valor)
  if (Number.isNaN(nascimento.getTime())) return ''
  const hoje = new Date()
  let meses =
    (hoje.getFullYear() - nascimento.getFullYear()) * 12 +
    hoje.getMonth() -
    nascimento.getMonth()
  if (hoje.getDate() < nascimento.getDate()) meses -= 1

  if (meses >= 12) {
    const anos = Math.floor(meses / 12)
    return `${anos} ${anos === 1 ? 'ano' : 'anos'}`
  }

  if (meses > 0) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`

  const dias = Math.max(
    0,
    Math.floor((hoje.getTime() - nascimento.getTime()) / 86400000),
  )
  return dias === 0 ? 'Recém-nascido' : `${dias} ${dias === 1 ? 'dia' : 'dias'}`
}

const sexoFormatado = (valor) =>
  valor === 'F' ? 'Feminino' : valor === 'M' ? 'Masculino' : valor || ''

const cpfFormatado = (valor) => {
  const cpf = String(valor || '').replace(/\D/g, '')
  if (cpf.length !== 11) return valor || ''
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`
}

const textoSeguro = (valor = '') =>
  String(valor)
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/•/g, '-')
    .replace(/[^\x20-\x7E\xA0-\xFF\r\n]/g, '')

const escapaPdf = (valor) =>
  textoSeguro(valor)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')

const componentesDaCor = (cor) => {
  const valor = String(cor || '').trim()
  const hex = valor.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1]
  if (hex) {
    const hexadecimal =
      hex.length === 3
        ? hex
            .split('')
            .map((componente) => componente.repeat(2))
            .join('')
        : hex
    return [0, 2, 4].map((posicao) =>
      Number.parseInt(hexadecimal.slice(posicao, posicao + 2), 16),
    )
  }

  const rgb = valor.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
  )
  if (!rgb) return null

  return rgb
    .slice(1, 4)
    .map((componente) => Math.max(0, Math.min(255, Number(componente))))
}

const corPdf = (cor) => {
  const componentes = componentesDaCor(cor)
  if (!componentes) return '0.11 0.11 0.11'
  return componentes
    .map((componente) => (componente / 255).toFixed(3))
    .join(' ')
}

const retanguloArredondado = (x, y, largura, altura, raio = 8) => {
  const controle = raio * 0.55228475
  const direita = x + largura
  const topo = y + altura
  return [
    `${x + raio} ${y} m`,
    `${direita - raio} ${y} l`,
    `${direita - raio + controle} ${y} ${direita} ${y + raio - controle} ${direita} ${y + raio} c`,
    `${direita} ${topo - raio} l`,
    `${direita} ${topo - raio + controle} ${direita - raio + controle} ${topo} ${direita - raio} ${topo} c`,
    `${x + raio} ${topo} l`,
    `${x + raio - controle} ${topo} ${x} ${topo - raio + controle} ${x} ${topo - raio} c`,
    `${x} ${y + raio} l`,
    `${x} ${y + raio - controle} ${x + raio - controle} ${y} ${x + raio} ${y} c`,
    'h S\n',
  ].join(' ')
}

const estiloDoNo = (no, estilo = {}) => {
  const tag = no.nodeName?.toLowerCase()
  const css = no.style || {}
  const proximo = { ...estilo }
  if (
    tag === 'b' ||
    tag === 'strong' ||
    css.fontWeight === 'bold' ||
    Number(css.fontWeight) >= 600
  )
    proximo.negrito = true
  if (css.fontWeight === 'normal') proximo.negrito = false
  if (tag === 'i' || tag === 'em' || css.fontStyle === 'italic')
    proximo.italico = true
  if (css.fontStyle === 'normal') proximo.italico = false
  if (tag === 'u' || css.textDecoration?.includes('underline'))
    proximo.sublinhado = true
  if (css.textDecoration === 'none') proximo.sublinhado = false
  const cor = css.color || no.getAttribute?.('color')
  if (componentesDaCor(cor)) proximo.cor = cor
  const tamanho = Number.parseFloat(css.fontSize || '')
  if (Number.isFinite(tamanho))
    proximo.tamanho = Math.max(7, Math.min(20, tamanho * 0.75))
  return proximo
}

const segmentos = (nos, estilo = {}) =>
  Array.from(nos).flatMap((no) => {
    if (no.nodeType === 3)
      return [{ texto: textoSeguro(no.textContent), estilo }]
    const tag = no.nodeName?.toLowerCase()
    if (tag === 'br') return [{ quebra: true }]

    const conteudo = segmentos(no.childNodes, estiloDoNo(no, estilo))
    // O contentEditable cria blocos <div> ou <p> quando o usuário pressiona
    // Enter. Eles precisam terminar uma linha no PDF, assim como um <br>.
    return ['p', 'div'].includes(tag)
      ? [...conteudo, { quebra: true }]
      : conteudo
  })

const blocoVazio = (bloco) =>
  ['p', 'div'].includes(bloco.nodeName?.toLowerCase()) &&
  !(bloco.textContent || '').replace(/\u00a0/g, ' ').trim()

// O Word costuma representar espaçamentos visuais como parágrafos vazios
// (por exemplo, <p><br></p>, <p>&nbsp;</p> ou <p><b>&nbsp;</b></p>). Mantemos
// somente um entre blocos preenchidos: isso preserva um enter entre itens da
// prescrição, sem acumular os vários vazios inseridos pelo Word.
export const normalizaConteudoReceituario = (html = '') => {
  const documento = new DOMParser().parseFromString(html, 'text/html')
  // No HTML normal, quebras no código-fonte são espaços, não Enter.
  // O Word as insere entre spans e palavras de um mesmo parágrafo.
  const textos = documento.createTreeWalker(documento.body, 4)
  while (textos.nextNode()) {
    textos.currentNode.textContent = textos.currentNode.textContent.replace(
      /[\t\r\n\f ]+/g,
      ' ',
    )
  }
  let encontrouConteudo = false
  let vazioPendente = null

  Array.from(documento.body.children).forEach((bloco) => {
    if (!blocoVazio(bloco)) {
      encontrouConteudo = true
      vazioPendente = null
      return
    }

    if (!encontrouConteudo || vazioPendente) {
      bloco.remove()
      return
    }

    vazioPendente = bloco
  })

  // Um vazio no fim não separa conteúdo e só desperdiçaria espaço no PDF.
  vazioPendente?.remove()

  return documento.body.innerHTML
}

const compactaQuebrasEntreBlocos = (segmentos) => {
  const resultado = []
  let quantidadeDeQuebras = 0

  segmentos.forEach((segmento) => {
    if (segmento.quebra) {
      // Uma quebra encerra a linha anterior e a segunda representa o único
      // enter visual permitido entre itens. As demais são sobras do Word ou
      // do contentEditable e não devem aumentar o espaço no receituário.
      if (quantidadeDeQuebras < 2) resultado.push(segmento)
      quantidadeDeQuebras += 1
      return
    }

    resultado.push(segmento)
    // Espaços e quebras de indentação presentes no HTML não representam uma
    // nova linha de conteúdo e não podem reiniciar a contagem.
    if (segmento.texto?.trim()) quantidadeDeQuebras = 0
  })

  return resultado
}

const segmentosDoHtml = (html) => {
  const documento = new DOMParser().parseFromString(
    normalizaConteudoReceituario(html),
    'text/html',
  )
  const resultado = []
  Array.from(documento.body.childNodes).forEach((no) => {
    const tag = no.nodeName?.toLowerCase()
    if (blocoVazio(no)) {
      resultado.push({ quebra: true })
      return
    }
    if (tag === 'ul' || tag === 'ol') {
      Array.from(no.children).forEach((item, indice) => {
        resultado.push(
          { texto: tag === 'ol' ? `${indice + 1}. ` : '- ', estilo: {} },
          ...segmentos(item.childNodes),
          { quebra: true },
        )
      })
    } else {
      resultado.push(...segmentos([no]))
    }
  })
  return compactaQuebrasEntreBlocos(resultado)
}

const fonte = (estilo) =>
  estilo.negrito ? (estilo.italico ? 'F4' : 'F2') : estilo.italico ? 'F3' : 'F1'
let contextoMedida
const larguraTexto = (texto, estilo) => {
  const tamanho = estilo.tamanho || 10.5
  if (typeof document === 'undefined') {
    return texto.length * tamanho * (estilo.negrito ? 0.56 : 0.51)
  }

  contextoMedida ||= document.createElement('canvas').getContext('2d')
  contextoMedida.font = `${estilo.italico ? 'italic ' : ''}${estilo.negrito ? 'bold ' : ''}${(tamanho * 96) / 72}px Arial`
  return contextoMedida.measureText(texto).width * (72 / 96)
}

const bytesParaString = (bytes) => {
  let resultado = ''
  for (let inicio = 0; inicio < bytes.length; inicio += 8192) {
    resultado += String.fromCharCode(...bytes.subarray(inicio, inicio + 8192))
  }
  return resultado
}

const dimensoesJpeg = (bytes) => {
  let posicao = 2
  while (posicao < bytes.length) {
    if (bytes[posicao] !== 0xff) {
      posicao += 1
      continue
    }
    const marcador = bytes[posicao + 1]
    const tamanho = (bytes[posicao + 2] << 8) + bytes[posicao + 3]
    if (marcador >= 0xc0 && marcador <= 0xc3) {
      return {
        altura: (bytes[posicao + 5] << 8) + bytes[posicao + 6],
        largura: (bytes[posicao + 7] << 8) + bytes[posicao + 8],
      }
    }
    posicao += 2 + tamanho
  }
  throw new Error('Imagem JPEG inválida')
}

const carregaJpeg = async (url) => {
  const resposta = await fetch(url)
  if (!resposta.ok)
    throw new Error('Não foi possível carregar a imagem do receituário')
  const bytes = new Uint8Array(await resposta.arrayBuffer())
  return { ...dimensoesJpeg(bytes), dados: bytesParaString(bytes) }
}

const carregaQrCodePng = (base64) =>
  new Promise((resolve, reject) => {
    const imagem = new Image()
    imagem.onerror = () =>
      reject(new Error('Não foi possível carregar o QR Code'))
    imagem.onload = () => {
      // O PDF o apresenta em um quadro quadrado. Criamos também uma imagem
      // quadrada para preservar as proporções do código, com resolução de
      // 240 dpi no tamanho impresso e sem ultrapassar o limite do Nginx.
      const tamanhoQr = 300
      const escala =
        tamanhoQr / Math.max(imagem.naturalWidth, imagem.naturalHeight)
      const largura = Math.max(1, Math.round(imagem.naturalWidth * escala))
      const altura = Math.max(1, Math.round(imagem.naturalHeight * escala))
      const canvas = document.createElement('canvas')
      canvas.width = tamanhoQr
      canvas.height = tamanhoQr
      const contexto = canvas.getContext('2d')
      contexto.imageSmoothingEnabled = false
      contexto.fillStyle = '#fff'
      contexto.fillRect(0, 0, tamanhoQr, tamanhoQr)
      contexto.drawImage(
        imagem,
        Math.round((tamanhoQr - largura) / 2),
        Math.round((tamanhoQr - altura) / 2),
        largura,
        altura,
      )
      const rgba = contexto.getImageData(0, 0, canvas.width, canvas.height).data
      let dados = ''
      for (let indice = 0; indice < rgba.length; indice += 4) {
        dados += String.fromCharCode(
          rgba[indice],
          rgba[indice + 1],
          rgba[indice + 2],
        )
      }
      resolve({ largura: canvas.width, altura: canvas.height, dados })
    }
    imagem.src = `data:image/png;base64,${base64}`
  })

const criaPaginas = (segmentosConteudo, especial = false) => {
  const paginas = [[]]
  let y = especial ? 524 : 660
  let x = margem
  let estiloAtual = {}
  const novaLinha = (altura = 16) => {
    y -= altura
    x = margem
    if (y < 55) {
      paginas.push([])
      y = especial ? 700 : 730
    }
  }
  const escrever = (texto, estilo) => {
    const tamanho = estilo.tamanho || 10.5
    const palavras = texto.split(/(\s+)/)
    palavras.forEach((palavra) => {
      if (!palavra) return
      const largura = larguraTexto(palavra, estilo)
      if (x + largura > margem + larguraUtil && x > margem)
        novaLinha(Math.max(15, tamanho * 1.45))
      if (palavra === '\n') return novaLinha(Math.max(15, tamanho * 1.45))
      paginas[paginas.length - 1].push({
        texto: palavra,
        x,
        y,
        estilo: { ...estilo, tamanho },
      })
      x += largura
    })
  }

  segmentosConteudo.forEach((segmento) => {
    if (segmento.quebra) return novaLinha(17)
    estiloAtual = segmento.estilo || estiloAtual
    escrever(segmento.texto, estiloAtual)
  })
  return paginas
}

const comandosDaPagina = (
  linhas,
  pagina,
  total,
  paciente,
  data,
  qrCode,
  urlValidacao,
  especial,
) => {
  const texto = (
    conteudo,
    x,
    y,
    tamanho = 10,
    fontePdf = 'F1',
    cor = '0.11 0.11 0.11',
  ) =>
    `BT /${fontePdf} ${tamanho} Tf ${cor} rg 1 0 0 1 ${x} ${y} Tm (${escapaPdf(conteudo)}) Tj ET\n`
  let comandos = 'q 24 0 0 29 36 801 cm /I1 Do Q\n'
  if (!especial || pagina > 1) comandos += 'q 96 0 0 25 36 770 cm /I2 Do Q\n'
  comandos += pagina === 1 ? '' : '0.68 G 36 756 m 559 756 l S\n'
  comandos += texto(
    'Assinado digitalmente por Dr. Edio Schaurich 411.588.060-00',
    360,
    830,
    7,
    'F1',
  )
  comandos += texto(`Data: ${data}`, 470, 818, 7, 'F1')
  comandos += texto('Dr. Edio Schaurich - CRM/RS 19557', 410, 804, 9, 'F2')
  if (especial) {
    if (pagina === 1) {
      const corTextoCabecalho = '0.11 0.11 0.11'
      const corBordaCabecalho = '0.345 0.651 0.553'
      const tituloReceituario = 'Receituário de Controle Especial'
      comandos += `${corBordaCabecalho} RG ${retanguloArredondado(36, 754, 523, 30, 8)}`
      comandos += texto(
        tituloReceituario,
        (larguraPagina -
          larguraTexto(tituloReceituario, { tamanho: 14, negrito: true })) /
          2,
        764,
        14,
        'F2',
        corTextoCabecalho,
      )
      const tituloEmitente = 'IDENTIFICAÇÃO DO EMITENTE'
      comandos += `${corBordaCabecalho} RG ${retanguloArredondado(36, 624, 380, 120)}`
      comandos += texto(tituloEmitente, 170, 720, 11, 'F2', corTextoCabecalho)
      comandos += 'q 96 0 0 25 48 672 cm /I2 Do Q\n'
      comandos += texto(
        'Dr. Édio Schaurich',
        170,
        703,
        13,
        'F2',
        corBordaCabecalho,
      )
      comandos += texto(
        'Médico - CRM/RS 19557',
        170,
        688,
        10,
        'F2',
        corTextoCabecalho,
      )
      comandos += texto(
        'Rua Buarque de Macedo, 1030/1000 - Montenegro/RS',
        170,
        667,
        8,
      )
      comandos += texto(
        'Fone: 51 98151-5077 - CPF: 411.588.060-00',
        170,
        649,
        8,
      )
      comandos += `${corBordaCabecalho} RG ${retanguloArredondado(422, 624, 137, 120)}`
      comandos += `${corBordaCabecalho} RG 436 684 m 545 684 l S\n`
      comandos += texto(
        '1ª VIA - FARMÁCIA',
        436,
        713,
        11,
        'F2',
        corTextoCabecalho,
      )
      comandos += texto(
        '2ª VIA - PACIENTE',
        436,
        646,
        11,
        'F2',
        corTextoCabecalho,
      )
      comandos += `${corBordaCabecalho} RG ${retanguloArredondado(36, 564, 523, 50)}`
      const linhaPacienteSuperior = 592
      const linhaPacienteInferior = 574
      comandos +=
        texto('Paciente:', 42, linhaPacienteSuperior, 10, 'F2') +
        texto(paciente.nmPaciente || '', 98, linhaPacienteSuperior, 10)
      comandos +=
        texto('Nascimento:', 320, linhaPacienteSuperior, 9, 'F2') +
        texto(
          dataFormatada(paciente.dtNascimento),
          390,
          linhaPacienteSuperior,
          9,
        )
      comandos +=
        texto('Idade:', 480, linhaPacienteSuperior, 9, 'F2') +
        texto(idade(paciente.dtNascimento), 515, linhaPacienteSuperior, 9)
      comandos +=
        texto('Telefone:', 42, linhaPacienteInferior, 10, 'F2') +
        texto(paciente.nrTelefone || '', 100, linhaPacienteInferior, 10)
      comandos +=
        texto('CPF:', 320, linhaPacienteInferior, 10, 'F2') +
        texto(cpfFormatado(paciente.cdCpf), 348, linhaPacienteInferior, 10)
      comandos += texto('Sexo:', 480, linhaPacienteInferior, 10, 'F2')
      comandos += texto(
        sexoFormatado(paciente.tpSexo),
        515,
        linhaPacienteInferior,
        10,
      )
      comandos += texto('PRESCRIÇÃO', 245, 544, 10, 'F2')
    }
  } else if (pagina === 1) {
    const corBordaQuadroPaciente = '0.345 0.651 0.553'
    const tituloReceituario = 'RECEITUÁRIO'
    const linhaPacienteSuperior = 703
    const linhaPacienteInferior = 685
    comandos += `${corBordaQuadroPaciente} RG ${retanguloArredondado(36, 735, 523, 30, 8)}`
    comandos += texto(
      tituloReceituario,
      (larguraPagina -
        larguraTexto(tituloReceituario, { tamanho: 14, negrito: true })) /
        2,
      745,
      14,
      'F2',
    )
    comandos += `${corBordaQuadroPaciente} RG ${retanguloArredondado(36, 675, 523, 50)}`
    comandos +=
      texto('Paciente:', 42, linhaPacienteSuperior, 10, 'F2') +
      texto(paciente.nmPaciente || '', 98, linhaPacienteSuperior, 10)
    comandos +=
      texto('Nascimento:', 320, linhaPacienteSuperior, 9, 'F2') +
      texto(dataFormatada(paciente.dtNascimento), 390, linhaPacienteSuperior, 9)
    comandos +=
      texto('Idade:', 480, linhaPacienteSuperior, 9, 'F2') +
      texto(idade(paciente.dtNascimento), 515, linhaPacienteSuperior, 9)
    comandos +=
      texto('Telefone:', 42, linhaPacienteInferior, 10, 'F2') +
      texto(paciente.nrTelefone || '', 100, linhaPacienteInferior, 10)
    comandos +=
      texto('CPF:', 320, linhaPacienteInferior, 10, 'F2') +
      texto(cpfFormatado(paciente.cdCpf), 348, linhaPacienteInferior, 10)
    comandos += texto('Sexo:', 480, linhaPacienteInferior, 10, 'F2')
    comandos += texto(
      sexoFormatado(paciente.tpSexo),
      515,
      linhaPacienteInferior,
      10,
    )
  }
  linhas.forEach(({ texto: conteudo, x, y, estilo }) => {
    const cor = corPdf(estilo.cor)
    comandos += texto(conteudo, x, y, estilo.tamanho, fonte(estilo), cor)
    if (estilo.sublinhado && conteudo.trim()) {
      const fim = x + larguraTexto(conteudo, estilo)
      comandos += `${cor} RG ${x} ${(y - 1).toFixed(2)} m ${fim.toFixed(2)} ${(y - 1).toFixed(2)} l S\n`
    }
  })
  if (pagina === total && !especial) {
    const dataExtenso = new Date().toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    // Agrupa as informações de autenticação e assinatura no rodapé. A versão
    // anterior distribuía esses blocos pela página inteira e criava grandes
    // espaços vazios entre cada linha.
    comandos += 'q 90 0 0 90 36 43 cm /I3 Do Q\n'
    comandos += texto(
      'Este documento foi gerado e assinado eletronicamente.',
      140,
      113,
      9,
    )
    comandos += texto('Para verificar a autenticidade, acesse:', 140, 97, 9)
    comandos += texto(urlValidacao, 140, 82, 8, 'F1', '0.05 0.24 0.55')
    const localData = `Montenegro, ${dataExtenso}.`
    comandos += texto(
      localData,
      (larguraPagina -
        larguraTexto(localData, { tamanho: 12, negrito: true })) /
        2,
      250,
      12,
      'F2',
    )
    const corBordaFinalizacao = '0.345 0.651 0.553'
    const larguraCarimbo = 235
    const centroCarimbo = larguraPagina / 2
    const nomeMedico = 'Dr. Edio Schaurich'
    const profissaoMedico = 'Médico'
    const registroMedico = 'CRM/RS 19557'
    comandos += `${corBordaFinalizacao} RG ${retanguloArredondado(
      centroCarimbo - larguraCarimbo / 2,
      145,
      larguraCarimbo,
      76,
    )}`
    comandos += texto(
      nomeMedico,
      centroCarimbo -
        larguraTexto(nomeMedico, { tamanho: 15, negrito: true }) / 2,
      197,
      15,
      'F2',
    )
    comandos += texto(
      profissaoMedico,
      centroCarimbo -
        larguraTexto(profissaoMedico, { tamanho: 14, negrito: true }) / 2,
      179,
      14,
      'F2',
    )
    comandos += texto(
      registroMedico,
      centroCarimbo -
        larguraTexto(registroMedico, { tamanho: 14, negrito: true }) / 2,
      162,
      14,
      'F2',
    )
  }
  comandos += '0.82 G 36 28 m 559 28 l S\n'
  if (especial && pagina === total) {
    const corBordaCarimbo = '0.345 0.651 0.553'
    const larguraCarimbo = 235
    const centroCarimbo = larguraPagina / 2
    const nomeMedico = 'Dr. Édio Schaurich'
    const profissaoMedico = 'Médico'
    const registroMedico = 'CRM/RS 19557'
    comandos += `${corBordaCarimbo} RG ${retanguloArredondado(
      centroCarimbo - larguraCarimbo / 2,
      250,
      larguraCarimbo,
      76,
    )}`
    comandos += texto(
      nomeMedico,
      centroCarimbo -
        larguraTexto(nomeMedico, { tamanho: 15, negrito: true }) / 2,
      302,
      15,
      'F2',
    )
    comandos += texto(
      profissaoMedico,
      centroCarimbo -
        larguraTexto(profissaoMedico, { tamanho: 14, negrito: true }) / 2,
      284,
      14,
      'F2',
    )
    comandos += texto(
      registroMedico,
      centroCarimbo -
        larguraTexto(registroMedico, { tamanho: 14, negrito: true }) / 2,
      267,
      14,
      'F2',
    )
    comandos += `q 64 0 0 64 36 174 cm /I3 Do Q\n`
    comandos += texto(
      'Este documento foi gerado e assinado eletronicamente.',
      108,
      222,
      7,
    )
    comandos += texto('Para verificar a autenticidade, acesse:', 108, 208, 7)
    comandos += texto(urlValidacao, 108, 194, 6, 'F1', '0.05 0.24 0.55')
    const corBordaQuadrosFinais = '0.345 0.651 0.553'
    comandos += `${corBordaQuadrosFinais} RG ${retanguloArredondado(36, 42, 250, 112)}`
    comandos += `${corBordaQuadrosFinais} RG ${retanguloArredondado(294, 42, 265, 112)}`
    comandos += texto('Identificação do Comprador', 104, 138, 10, 'F2')
    comandos += texto('Identificação do Fornecedor', 368, 138, 10, 'F2')
    comandos += texto('Nome:', 44, 116, 9)
    comandos += texto('Identidade:', 44, 98, 9)
    comandos += texto('Órgão Emissor:', 160, 98, 9)
    comandos += texto('Endereço:', 44, 80, 9)
    comandos += texto('Cidade:', 44, 62, 9)
    comandos += texto('Telefone:', 44, 44, 9)
    comandos += `${corBordaQuadrosFinais} RG 320 82 m 456 82 l S\n`
    comandos += `${corBordaQuadrosFinais} RG 486 82 m 532 82 l S\n`
    comandos += texto('ASSINATURA DO FARMACÊUTICO', 328, 66, 8, 'F2')
    comandos += texto('DATA', 499, 66, 8, 'F2')
  }
  const paginacao = `Página ${pagina}/${total}`
  comandos += texto(
    'SPA TOUR LIFE - Rua Buarque de Macedo, 1030/1000 - Montenegro/RS',
    36,
    17,
    7,
    'F1',
    '0.45 0.45 0.45',
  )
  comandos += texto(
    paginacao,
    larguraPagina - margem - larguraTexto(paginacao, { tamanho: 7 }),
    17,
    7,
    'F1',
    '0.45 0.45 0.45',
  )
  return comandos
}

export const geraPdfReceituarioSimples = async ({
  paciente = {},
  dsConteudo = '',
  dsObservacao = '',
  qrCodeBase64,
  urlValidacao = '',
  especial = false,
}) => {
  const observacaoCompacta = normalizaConteudoReceituario(dsObservacao)
  const segmentosConteudo = segmentosDoHtml(
    [normalizaConteudoReceituario(dsConteudo), observacaoCompacta]
      .filter(Boolean)
      .join('<br><br>'),
  )
  const paginas = criaPaginas(segmentosConteudo, especial)
  if (!especial) {
    // O rodapé de validação ocupa a faixa inferior da última página. Só cria
    // uma página exclusiva para ele quando a prescrição já usa essa área.
    const ultimaPagina = paginas[paginas.length - 1]
    const ultimaLinha = ultimaPagina.reduce(
      (menorY, linha) => Math.min(menorY, linha.y),
      alturaPagina,
    )
    if (ultimaLinha < 275) paginas.push([])
  } else if (paginas[paginas.length - 1].some((linha) => linha.y < 335)) {
    // A finalização do controle especial inclui assinatura, QR Code e os
    // quadros de identificação. Reserva uma faixa maior para esses itens.
    paginas.push([])
  }
  const data = new Date().toLocaleString('pt-BR')
  const objetos = [
    null,
    '<< /Type /Catalog /Pages 2 0 R >>',
    null,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-BoldOblique /Encoding /WinAnsiEncoding >>',
  ]
  const [logoIcp, logoSpa] = await Promise.all([
    carregaJpeg('/receituario/logo-icp-brasil.jpg'),
    carregaJpeg('/receituario/logo-spa.jpg'),
  ])
  const qrCode = await carregaQrCodePng(qrCodeBase64)
  objetos.push(
    `<< /Type /XObject /Subtype /Image /Width 255 /Height 100 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logoIcp.dados.length} >>\nstream\n${logoIcp.dados}\nendstream`,
  )
  objetos.push(
    `<< /Type /XObject /Subtype /Image /Width 255 /Height 100 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logoSpa.dados.length} >>\nstream\n${logoSpa.dados}\nendstream`,
  )
  objetos.push(
    `<< /Type /XObject /Subtype /Image /Width ${qrCode.largura} /Height ${qrCode.altura} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Length ${qrCode.dados.length} >>\nstream\n${qrCode.dados}\nendstream`,
  )
  const paginasIds = []
  paginas.forEach((linhas, indice) => {
    const idPagina = objetos.length
    const idConteudo = idPagina + 1
    paginasIds.push(idPagina)
    const comandos = comandosDaPagina(
      linhas,
      indice + 1,
      paginas.length,
      paciente,
      data,
      qrCode,
      urlValidacao,
      especial,
    )
    objetos.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${larguraPagina} ${alturaPagina}] /Resources << /Font << /F1 3 0 R /F2 4 0 R /F3 5 0 R /F4 6 0 R >> /XObject << /I1 7 0 R /I2 8 0 R /I3 9 0 R >> >> /Contents ${idConteudo} 0 R >>`,
    )
    objetos.push(
      `<< /Length ${comandos.length} >>\nstream\n${comandos}endstream`,
    )
  })
  objetos[2] = `<< /Type /Pages /Kids [${paginasIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${paginasIds.length} >>`
  let arquivo = '%PDF-1.4\n%\xE2\xE3\xCF\xD3\n'
  const offsets = [0]
  objetos.slice(1).forEach((objeto, indice) => {
    offsets.push(arquivo.length)
    arquivo += `${indice + 1} 0 obj\n${objeto}\nendobj\n`
  })
  const inicioXref = arquivo.length
  arquivo += `xref\n0 ${objetos.length}\n0000000000 65535 f \n${offsets
    .slice(1)
    .map((offset) => `${String(offset).padStart(10, '0')} 00000 n `)
    .join(
      '\n',
    )}\ntrailer\n<< /Size ${objetos.length} /Root 1 0 R >>\nstartxref\n${inicioXref}\n%%EOF`
  return btoa(arquivo)
}
